import { nameParts, normalisePhone, searchByName, type Family, type FamilyChild, type FamilyGroup, type NameText, type OwnerWaiting } from "@teamna/shared";

/**
 * The Families page, as data (onboarding version 2, 2026-10-04). Pure functions, so the parser,
 * the filters, "place by age" and the invite text are tested without a screen.
 */

/** The business's own words, lower case: "level", "levels", "swimmer", "coach", "parent". */
export type Words = {
  group: string;
  groups: string;
  participant: string;
  participants: string;
  staff: string;
  member: string;
  members: string;
  /**
   * Members book for their children (`booking_for = children`). False where clients book for
   * themselves (F2): the text then names no child and no family. Read from `useShape()`.
   */
  forChildren: boolean;
};

/**
 * The word for the people on one account: "family" or "families" where members book for their
 * children, the member word ("client", "clients") where clients book for themselves (F2).
 */
export function householdWord(count: number, words: Words): string {
  if (!words.forChildren) return count === 1 ? words.member : words.members;
  return count === 1 ? "family" : "families";
}

export type NewFamily = {
  adults: Array<{ name: string; phone: string }>;
  children: Array<{ name: string; age: number | null }>;
};

/** "Mira 6", "Mira (6)", "Mira, 6", or just "Mira". */
export function parseChild(text: string): { name: string; age: number | null } | null {
  const value = text.trim();
  if (!value) return null;
  const match = /^(.*?)[\s,]*\(?(\d{1,2})\)?$/.exec(value);
  const name = match?.[1]?.trim();
  if (match && name) return { name, age: Number(match[2]) };
  return { name: value, age: null };
}

/**
 * One family on each line: the parents and their numbers, a colon, then the children. Two parents
 * are joined with "&" or "/". The age is optional.
 *
 *   Huda Saleh 050 555 1010 & Faisal Saleh 055 555 2020: Mira 6, Ali 9
 *   Salem Juma 056 909 1212
 */
export function parseFamilyList(text: string): NewFamily[] {
  const out: NewFamily[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const cut = line.indexOf(":");
    const left = (cut >= 0 ? line.slice(0, cut) : line).trim();
    const right = cut >= 0 ? line.slice(cut + 1).trim() : "";
    const adults = left
      .split(/\s*[&/]\s*/)
      .filter(Boolean)
      .map((part) => {
        const match = /^(.*?)([+\d][\d\s()-]{6,})$/.exec(part.trim());
        return {
          name: (match ? match[1]! : part).replace(/[,-]\s*$/, "").trim(),
          phone: match ? match[2]!.trim() : "",
        };
      })
      .filter((adult) => adult.name || adult.phone)
      .slice(0, 2);
    if (adults.length === 0) continue;
    const children = right
      ? right
          .split(",")
          .map(parseChild)
          .filter((child): child is { name: string; age: number | null } => child !== null)
      : [];
    out.push({ adults, children });
  }
  return out;
}

export const SAMPLE_LIST =
  "Huda Saleh 050 555 1010 & Faisal Saleh 055 555 2020: Mira 6, Ali 9\nOmar Bakr 052 222 3344: Yara 8\nSalem Juma 056 909 1212";

export function familyName(family: Family): string {
  return family.adults.map((adult) => adult.name).filter(Boolean).join(" and ") || "No name";
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

export function familyHasNumber(family: Family): boolean {
  return family.adults.some((adult) => !!adult.phoneE164);
}

export function isPlaced(child: FamilyChild, groups: FamilyGroup[]): boolean {
  return child.groupId !== null && groups.some((group) => group.id === child.groupId);
}

export function groupOf(child: FamilyChild, groups: FamilyGroup[]): FamilyGroup | null {
  return child.groupId ? (groups.find((group) => group.id === child.groupId) ?? null) : null;
}

/** "Level 1", "Not sure yet", "No level". */
export function childGroupText(child: FamilyChild, groups: FamilyGroup[], words: Words): string {
  const group = groupOf(child, groups);
  if (group) return group.name;
  return child.placementStatus === "unsure" ? "Not sure yet" : `No ${words.group}`;
}

/** "050 123 4567" for a UAE number, the full international number otherwise. */
export function phoneText(phoneE164: string | null): string {
  if (!phoneE164) return "No number";
  const uae = /^\+971(\d{2})(\d{3})(\d{4})$/.exec(phoneE164);
  return uae ? `0${uae[1]} ${uae[2]} ${uae[3]}` : phoneE164;
}

// --- filters and search -----------------------------------------------------

export type FamilyFilter = "all" | "nogroup" | "nophone" | "onhold" | "notinvited";

export function filterTest(filter: FamilyFilter, groups: FamilyGroup[]): (family: Family) => boolean {
  switch (filter) {
    case "nogroup":
      return (family) => family.children.some((child) => !isPlaced(child, groups));
    case "nophone":
      return (family) => !familyHasNumber(family);
    case "onhold":
      return (family) => family.holdNote !== null;
    case "notinvited":
      return (family) => family.invite === "none";
    default:
      return () => true;
  }
}

/** Digits a person typed, as the number is stored: "050 123" also finds "+97150123...". */
export function digitsMatch(phoneE164: string | null | undefined, query: string): boolean {
  const digits = query.replace(/\D/g, "");
  if (!phoneE164 || digits.length < 3) return false;
  const stored = phoneE164.replace(/\D/g, "");
  return stored.includes(digits) || (digits.startsWith("0") && stored.includes(`971${digits.slice(1)}`));
}

/**
 * What finds a child in a name search: the child's name; the name with the group, so a group
 * alone or with a name finds the child; and the names of the adults, a little lower.
 */
export function childSearchTexts(name: string, groupText: string | null | undefined, adults: readonly { name: string }[]): NameText[] {
  return [name, { text: nameParts(name, groupText), via: true }, ...adults.map((adult) => ({ text: adult.name, via: true as const }))];
}

/** Families by name (the adults and the children, by the name search rule, best first) or by number. */
export function searchFamilies<T>(rows: readonly T[], query: string, familyOf: (row: T) => Family) {
  return searchByName(rows, query, (row) => [...familyOf(row).adults.map((adult) => adult.name), ...familyOf(row).children.map((child) => child.name)], {
    extra: (row) => familyOf(row).adults.some((adult) => digitsMatch(adult.phoneE164, query)),
  });
}

export type ParticipantRow = { child: FamilyChild; family: Family };

export function participantRows(families: Family[]): ParticipantRow[] {
  return families
    .flatMap((family) => family.children.map((child) => ({ child, family })))
    .sort((a, b) => a.child.name.localeCompare(b.child.name));
}

/** One row of the swimmers list: a child with a parent, or a name a coach added that waits for one. */
export type ParticipantListRow = { kind: "child"; name: string; row: ParticipantRow } | { kind: "waiting"; name: string; row: OwnerWaiting };

/**
 * The swimmers list by name, group or adult (the name search rule, best first), or by an adult's
 * number. A name that waits for a parent (`GET .../new-participants`) is found by its name, its
 * level, or the parent's name and number from the coach's note.
 */
export function searchParticipants(rows: readonly ParticipantListRow[], query: string, groups: FamilyGroup[], words: Words) {
  return searchByName(
    rows,
    query,
    (item) =>
      item.kind === "child"
        ? childSearchTexts(item.row.child.name, childGroupText(item.row.child, groups, words), item.row.family.adults)
        : childSearchTexts(item.row.name, item.row.groupName, item.row.noteParentName ? [{ name: item.row.noteParentName }] : []),
    {
      extra: (item) =>
        item.kind === "child" ? item.row.family.adults.some((adult) => digitsMatch(adult.phoneE164, query)) : digitsMatch(item.row.notePhoneE164, query),
    },
  );
}

// --- the groups board -------------------------------------------------------

export type BoardColumn = "none" | "unsure" | string;

export function columnOf(child: FamilyChild, groups: FamilyGroup[]): BoardColumn {
  if (isPlaced(child, groups)) return child.groupId!;
  return child.placementStatus === "unsure" ? "unsure" : "none";
}

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "17:00" to "5:00 pm". */
export function clockText(time: string): string {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  const suffix = hours >= 12 ? "pm" : "am";
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

/** "Sun and Tue, 5:00 pm", or each day with its time when they differ. */
export function slotsText(group: FamilyGroup): string {
  if (group.slots.length === 0) return "No weekly sessions yet";
  const times = new Set(group.slots.map((slot) => slot.startTime));
  if (times.size === 1) {
    const days = [...new Set(group.slots.map((slot) => WEEKDAY[slot.weekday]!))];
    const joined = days.length > 1 ? `${days.slice(0, -1).join(", ")} and ${days.at(-1)}` : days[0];
    return `${joined}, ${clockText(group.slots[0]!.startTime)}`;
  }
  return group.slots.map((slot) => `${WEEKDAY[slot.weekday]} ${clockText(slot.startTime)}`).join(", ");
}

/** "Coach Karim. Sun and Tue, 5:00 pm." */
export function groupLine(group: FamilyGroup, words: Words): string {
  const staff = group.staff ? `${capital(words.staff)} ${firstName(group.staff.name)}. ` : "";
  return `${staff}${slotsText(group)}.`;
}

export function capital(word: string): string {
  return word ? word[0]!.toUpperCase() + word.slice(1) : word;
}

/**
 * "Place by age": a child with an age and no group goes to the group whose placed children are
 * that age. Nothing is guessed for an age no group has yet, and a child without an age stays.
 */
export function placeByAge(families: Family[], groups: FamilyGroup[]): Array<{ participantId: string; groupId: string; name: string }> {
  const ranges = groups
    .map((group) => {
      const ages = families
        .flatMap((family) => family.children)
        .filter((child) => child.groupId === group.id && child.age !== null)
        .map((child) => child.age!);
      if (ages.length === 0) return null;
      return {
        groupId: group.id,
        min: Math.min(...ages),
        max: Math.max(...ages),
        mean: ages.reduce((sum, age) => sum + age, 0) / ages.length,
      };
    })
    .filter((range): range is { groupId: string; min: number; max: number; mean: number } => range !== null);

  const out: Array<{ participantId: string; groupId: string; name: string }> = [];
  for (const family of families) {
    for (const child of family.children) {
      if (child.age === null || isPlaced(child, groups) || child.placementStatus === "unsure") continue;
      const fits = ranges
        .filter((range) => child.age! >= range.min && child.age! <= range.max)
        .sort((a, b) => Math.abs(a.mean - child.age!) - Math.abs(b.mean - child.age!));
      if (fits[0]) out.push({ participantId: child.participantId, groupId: fits[0].groupId, name: child.name });
    }
  }
  return out;
}

/** What "Place by age" goes by, for the button's hint: "Starters 4 to 5, Level 1 6 to 7". */
export function ageRangesText(families: Family[], groups: FamilyGroup[]): string {
  return groups
    .map((group) => {
      const ages = families
        .flatMap((family) => family.children)
        .filter((child) => child.groupId === group.id && child.age !== null)
        .map((child) => child.age!);
      if (ages.length === 0) return null;
      const min = Math.min(...ages);
      const max = Math.max(...ages);
      return `${group.name} ${min === max ? min : `${min} to ${max}`}`;
    })
    .filter(Boolean)
    .join(", ");
}

// --- invites ----------------------------------------------------------------

export function inviteReady(family: Family, groups: FamilyGroup[], words: Words): boolean {
  if (!words.forChildren) return familyHasNumber(family) && family.invite === "none";
  return (
    familyHasNumber(family) &&
    family.invite === "none" &&
    family.children.length > 0 &&
    family.children.every((child) => isPlaced(child, groups))
  );
}

export function canInvite(family: Family): boolean {
  return familyHasNumber(family) && family.invite !== "joined";
}

/** What the owner should know before inviting this family, or null. */
export function inviteWarning(family: Family, groups: FamilyGroup[], words: Words): string | null {
  if (!familyHasNumber(family)) return `No number. This ${householdWord(1, words)} cannot get an invite yet.`;
  if (!words.forChildren) return null;
  const missing = family.children.filter((child) => !isPlaced(child, groups)).map((child) => child.name);
  if (missing.length) {
    const who = missing.length === 1 ? missing[0]! : `${missing.slice(0, -1).join(", ")} and ${missing.at(-1)}`;
    return `${who} ${missing.length === 1 ? "has" : "have"} no ${words.group}. The ${words.member} sees no sessions for ${missing.length === 1 ? "this child" : "them"} yet.`;
  }
  if (family.children.length === 0) return `No children yet. The ${words.member} adds them after joining.`;
  return null;
}

/** The message one adult reads: their children, each child's group and its days. */
export function inviteMessage(
  family: Family,
  adult: { name: string },
  groups: FamilyGroup[],
  input: { businessName: string; url: string; code: string; words: Words },
): string {
  const lines = family.children.map((child) => {
    const group = groupOf(child, groups);
    if (!group) return `We tell you ${child.name}'s ${input.words.group} soon.`;
    const staff = group.staff ? ` with ${capital(input.words.staff)} ${firstName(group.staff.name)}` : "";
    return `${child.name} is in ${group.name}${staff}. Sessions: ${slotsText(group)}. Book the ones you want in the app.`;
  });
  const next = !input.words.forChildren
    ? "Book your first session in the app. It takes one minute."
    : family.children.length
      ? lines.join("\n")
      : `Add your children in the app. It takes one minute.`;
  return [
    `Hi ${firstName(adult.name) || "there"}. ${input.businessName} now uses Teamna for bookings and payments.`,
    next,
    `Join with the code ${input.code}: ${input.url}`,
  ].join("\n\n");
}

// --- WhatsApp ---------------------------------------------------------------

/** A wa.me link with the message ready to send, from the owner's own WhatsApp. */
export function whatsappHref(phoneE164: string, text?: string): string {
  const digits = (normalisePhone(phoneE164) ?? phoneE164).replace(/\D/g, "");
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
}

/** The first line of a message to the staff member about one child. */
export function staffMessage(childName: string, groupName: string | null): string {
  return `Hi. About ${childName}${groupName ? ` in ${groupName}` : ""}: `;
}

// --- the welcome card -------------------------------------------------------

/** "Last time, on Thursday at 9:40 pm, you added 8 families and put 5 swimmers in a level." */
export function lastSittingText(
  sitting: { at: string; familiesAdded: number; placed: number },
  timezone: string,
  words: Words,
): string | null {
  const parts: string[] = [];
  if (sitting.familiesAdded) parts.push(`added ${sitting.familiesAdded} ${householdWord(sitting.familiesAdded, words)}`);
  if (sitting.placed && words.forChildren) {
    parts.push(`put ${sitting.placed} ${sitting.placed === 1 ? words.participant : words.participants} in a ${words.group}`);
  }
  if (parts.length === 0) return null;
  return `Last time, on ${dayAndTime(sitting.at, timezone)}, you ${parts.join(" and ")}.`;
}

/** "Thursday at 9:40 pm" in the business timezone. */
export function dayAndTime(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  }).formatToParts(new Date(iso));
  const part = (type: string) => parts.find((row) => row.type === type)?.value ?? "";
  return `${part("weekday")} at ${part("hour")}:${part("minute")} ${part("dayPeriod").toLowerCase()}`;
}
